import { ChangeDetectionStrategy, Component, DestroyRef, HostListener, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { AppDatePipe } from '../../../helpers/app-date.pipe';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { IconComponent, TypographyComponent } from '@totalenergiescode/tds-angular';
import { interval, map, startWith, switchMap } from 'rxjs';

import { AppNotification, NotificationsService } from '../../../services/notifications/notifications.service';

/**
 * Notification bell component — shows unread count badge, opens dropdown list.
 * Polls the notifications endpoint at a fixed interval for near-live updates.
 *
 * @remarks
 * - Bell icon with badge for unread count (FR-021)
 * - Clicking a notification marks it as read (FR-021)
 * - Dropdown shows: event type, contractor name + IGG, outcome, timestamp (FR-023)
 */
@Component({
  selector: 'app-notification-bell',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './notification-bell.component.html',
  styleUrl: './notification-bell.component.scss',
  imports: [TranslateModule, IconComponent, TypographyComponent, AppDatePipe],
})
export class NotificationBellComponent implements OnInit {
  private readonly _notificationsService = inject(NotificationsService);
  private readonly _translateService = inject(TranslateService);
  private readonly _destroyRef = inject(DestroyRef);

  // ─── Proxied signals ────────────────────────────────────────────────────────

  readonly notifications = this._notificationsService.notifications;
  readonly unreadCount = this._notificationsService.unreadCount;
  readonly isDropdownOpen = this._notificationsService.isDropdownOpen;

  /**
   * Current language signal — changes when user switches language.
   * Used to trigger re-evaluation of translated notification messages.
   */
  readonly currentLang = toSignal(this._translateService.onLangChange.pipe(map((e) => e.lang)), {
    initialValue: this._translateService.currentLang,
  });

  // ─── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    // Fetch immediately, then re-poll whenever the interval changes (normal 2min / burst 10s)
    this._notificationsService.pollInterval$
      .pipe(
        switchMap((ms) => interval(ms).pipe(startWith(0))),
        switchMap(() => this._notificationsService.loadNotifications()),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((notifications) => this._notificationsService.setNotifications(notifications));
  }

  // ─── Translation helpers ────────────────────────────────────────────────────

  /**
   * Resolve the human-readable message for a notification.
   *
   * Resolution order:
   * 1. If `message_key` is present, translate it.
   *    For failure notifications, first resolve `reason_key` and inject the result
   *    as the `reason` param of the outer key.
   * 2. Fall back to `data.message` (legacy pre-rendered text).
   * 3. Fall back to `data.outcome` (legacy pre-rendered text).
   *
   * The `currentLang` signal dependency ensures this re-evaluates on language switch.
   */
  resolveMessage(notification: AppNotification): string {
    // Touch currentLang so Angular's signal graph re-evaluates on language changes.
    // The actual translation uses instant() which reads the current language internally.
    void this.currentLang();

    const { message_key, message_params, reason_key, message, outcome } = notification.data;

    if (message_key) {
      let params = { ...(message_params ?? {}) };

      // Resolve nested reason_key first, then inject as the `reason` param
      if (reason_key) {
        params = { ...params, reason: this._translateService.instant(reason_key) };
      }

      // `message_key` arrives fully qualified from the backend (e.g. 'notifications.renewal_success').
      // Do NOT prefix it again — that yields a missing key and ngx-translate echoes the raw input.
      return this._translateService.instant(message_key, params);
    }

    return message ?? outcome ?? '';
  }

  /**
   * Resolve the title for bulk notifications.
   * Falls back to `data.title` (legacy) when no message_key is present.
   */
  resolveTitle(notification: AppNotification): string {
    void this.currentLang();

    const { message_key, title } = notification.data;

    // `message_key` is fully qualified (e.g. 'notifications.bulk_renewal_success'),
    // so match on the qualified prefix rather than a bare 'bulk_'.
    if (message_key?.startsWith('notifications.bulk_')) {
      return this._translateService.instant('notifications.bulk_renewal_title');
    }

    return title ?? this._translateService.instant('notifications.unknown_type');
  }

  // ─── Actions ────────────────────────────────────────────────────────────────

  toggleDropdown(): void {
    this._notificationsService.toggleDropdown();
  }

  markAsRead(notificationId: string): void {
    this._notificationsService.markAsRead(notificationId).pipe(takeUntilDestroyed(this._destroyRef)).subscribe();
  }

  markAllAsRead(): void {
    this._notificationsService.markAllAsRead();
  }

  /** Close dropdown when clicking outside */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.notification-bell')) {
      this._notificationsService.closeDropdown();
    }
  }
}
